import { useWorkspaceText } from '../interface-copy';
import { TemplateGallery } from '../company-feed';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ArchiveIcon, PowerOffIcon } from 'lucide-react';
import { useCompany } from '@/company/context';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { OverflowMenu, RowAction, splitRowActions, type OverflowItem } from '@/components/ui/menu';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPresentationCopy from '@/i18n/ns/agent-presentation';
import { overlaysCopy, type OverlaysCopyKey } from '@/i18n/ns/overlays';
import workspaceShellCopy from '@/i18n/ns/workspace-shell';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { ErrorState, LoadingState, PageHeading, StartInChatButton } from '../primitives';
import { beginAgentCreation } from './authoring';
import {
  agentErrorMessage,
  agentsApi,
  knownAgentError,
  onAgentsChanged,
  type AgentGroup,
  type AgentListRow,
} from './api';
import { agentStatus, scheduleLine } from './schedule';
import { lastRunDay } from './time';
import { ScopeChip } from './shared';
import './agents.css';

// The Agents page (collaboration v1, PR 4 §7): one list, no tabs. Your own
// agents first, then the company's, then (admins only) other people's personal
// agents — separated by a thin labelled line — with one search field.

const SEARCH_DELAY_MS = 250;

function useAgentList(query: string, scope: string | null, locale: string) {
  const [state, setState] = useState({
    agents: [] as AgentListRow[],
    loading: true,
    error: '',
  });
  const [tick, setTick] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    if (!scope) {
      setState({ agents: [], loading: true, error: '' });
      return;
    }
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    const timer = setTimeout(
      () => {
        agentsApi
          .list(query)
          .then((agents) => {
            if (current === generation.current) setState({ agents, loading: false, error: '' });
          })
          .catch((failure) => {
            if (current === generation.current)
              setState({ agents: [], loading: false, error: agentErrorMessage(failure, locale) });
          });
      },
      query ? SEARCH_DELAY_MS : 0,
    );
    return () => {
      clearTimeout(timer);
      generation.current++;
    };
  }, [query, scope, tick, locale]);
  const refresh = useCallback(() => setTick((value) => value + 1), []);
  useEffect(() => onAgentsChanged(refresh), [refresh]);
  return { ...state, refresh };
}

/** Its latest run waits for the viewer's own approval (the list's "Needs you"). */
const needsYou = (row: AgentListRow, viewerId: string | undefined) =>
  row.lastRun?.status === 'awaiting_approval' && row.lastRun.runBy?.userId === viewerId;

/** "Last run today 07:03", "Last run Thursday · failed", "Never run". */
function lastRunLabel(
  row: AgentListRow,
  copy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>>,
) {
  if (!row.lastRun) return copy.neverRun;
  const at = row.lastRun.finishedAt ?? row.lastRun.startedAt ?? row.lastRun.createdAt;
  const failed = row.lastRun.status === 'failed' ? ' · failed' : '';
  return at
    ? formatCopy(copy.lastRun, { date: lastRunDay(at), failed })
    : formatCopy(copy.lastRunOnly, { failed });
}

// No owner: a company agent passed to the company when its owner left; a
// personal one lost its owner when their account was deleted.
function ownerLabel(
  row: AgentListRow,
  companyName: string,
  copy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>>,
) {
  if (row.owner) return row.group === 'yours' ? copy.you : (row.owner.name ?? copy.formerMember);
  return row.scope === 'company'
    ? formatCopy(copy.adminsManage, { company: companyName })
    : copy.formerMember;
}

// An admin's row for another person's personal agent: names only.
function othersDetail(
  row: AgentListRow,
  schedule: string,
  copy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>>,
) {
  if (!row.owner) return formatCopy(copy.namesDeleted, { schedule });
  return formatCopy(copy.namesRuns, { schedule, name: row.owner.name ?? copy.formerMember });
}

type RowHandlers = {
  onRun: (row: AgentListRow) => void;
  onSwitchOff: (row: AgentListRow) => void;
  onArchive: (row: AgentListRow) => void;
};
type OverflowCopy = Readonly<Record<OverlaysCopyKey, string>>;

// The row opens the agent. Its other actions follow DESIGN-SPEC §6.6: one is a
// quiet button on the row, two or more sit behind ⋯ (PL-31). Yours and the
// company's run. Admins switch another person's personal agent off, and archive
// one whose owner's account was deleted (nobody else can open it).
function rowActions(
  row: AgentListRow,
  copy: OverflowCopy,
  agentCopy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>>,
  handlers: RowHandlers,
) {
  const items: OverflowItem[] = [];
  if (row.group !== 'others' && row.access.run)
    items.push({
      key: 'run',
      label: copy.runNow,
      description: copy.runNowDetail,
      reason: row.publishedRevision ? undefined : copy.publishFirst,
      onSelect: () => handlers.onRun(row),
    });
  if (row.group === 'others' && row.enabled)
    items.push({
      key: 'switch-off',
      label: agentCopy.switchOff,
      icon: <PowerOffIcon aria-hidden="true" />,
      onSelect: () => handlers.onSwitchOff(row),
    });
  if (row.group === 'others' && !row.owner)
    items.push({
      key: 'archive',
      label: agentCopy.archive,
      icon: <ArchiveIcon aria-hidden="true" />,
      tone: 'danger',
      separated: items.length > 0,
      onSelect: () => handlers.onArchive(row),
    });
  return items;
}

/** On, Off, On demand or Needs you (agent redesign 2026-10-09). */
function AgentStatusPill({
  scheduled,
  enabled,
  waiting,
}: {
  scheduled: boolean;
  enabled: boolean;
  waiting: boolean;
}) {
  const status = agentStatus(scheduled, enabled, useNamespaceCopy(overlaysCopy), waiting);
  return (
    <span className="ws-agents-pill" data-status={status.kind}>
      {status.label}
    </span>
  );
}

function AgentRow({ row, ...handlers }: { row: AgentListRow } & RowHandlers) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const shellCopy = useNamespaceCopy(workspaceShellCopy);
  const ws = useWorkspace();
  const copy = useNamespaceCopy(overlaysCopy);
  const locale = useChatInterfaceLocale();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const others = row.group === 'others';
  const waiting = !others && needsYou(row, ws.user?.id);
  const schedule = scheduleLine(row.schedule, row.enabled, copy, locale);
  const detail = others
    ? othersDetail(row, schedule, agentCopy)
    : [
        row.publishedRevision === null ? agentCopy.draftNotCreated : null,
        schedule,
        waiting ? agentCopy.waiting : lastRunLabel(row, agentCopy),
      ]
        .filter(Boolean)
        .join(' · ');
  const open = () => ws.openCanvas({ kind: 'agent', id: row.id });
  const title = useId();
  const { single, menu } = splitRowActions(rowActions(row, copy, agentCopy, handlers));
  return (
    <article
      className={`ws-agents-row pc-reveal-host${others ? ' others' : ''}`}
      data-agent={row.id}
    >
      <button
        className="ws-agents-row-main"
        disabled={others}
        aria-label={others ? row.name : uiText('Open agent: {value1}', { value1: row.name })}
        onClick={open}
      >
        <span className="ws-record-symbol">
          <WorkspaceIcon name="agents" size={22} />
        </span>
        <span className="ws-agents-row-copy">
          <strong id={title}>{row.name}</strong>
          <small className="ws-agents-row-who">
            <ScopeChip scope={row.scope} />{' '}
            <span>{ownerLabel(row, ws.companyName, agentCopy)}</span>
            {row.group === 'company' && row.access.edit && <span> {uiText('· you can edit')}</span>}
          </small>
          <small className="ws-agents-row-detail">{detail}</small>
        </span>
      </button>
      {/* The quiet action sits before the status, and the ⋯ column is kept, so
          On/Off lines up from row to row. */}
      <span className="ws-agents-row-actions">
        {single && <RowAction item={single} describedBy={title} />}
        <AgentStatusPill
          scheduled={Boolean(row.schedule)}
          enabled={row.enabled}
          waiting={waiting}
        />
        <OverflowMenu
          label={formatCopy(copy.moreActionsFor, { name: row.name })}
          items={menu}
          reserveSlot
        />
      </span>
    </article>
  );
}

function GroupLine({ label }: { label: string }) {
  return (
    <div className="ws-agents-group" role="separator" aria-label={label}>
      <span>{label}</span>
    </div>
  );
}

const GROUPS: { group: AgentGroup; label: string | null }[] = [
  { group: 'yours', label: null },
  { group: 'company', label: 'From your company' },
  { group: 'others', label: 'Personal agents of others · visible to admins' },
];

export function AgentsPage() {
  const locale = useChatInterfaceLocale();
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const [query, setQuery] = useState('');
  const scope =
    ws.user?.id && company.data?.company?.id && company.data.membership?.userId === ws.user.id
      ? `${ws.user.id}\0${company.data.company.id}`
      : null;
  const list = useAgentList(query.trim(), scope, locale);
  const copy = useNamespaceCopy(overlaysCopy);
  const shellCopy = useNamespaceCopy(workspaceShellCopy);
  const [archiving, setArchiving] = useState<{ row: AgentListRow; busy: boolean } | null>(null);
  // Outcomes are toasts at the bottom of the column, next to the work: a
  // failure stays until it is dismissed and offers to try again, so its text
  // says what failed once ("Couldn't run …") unless the server said why.
  const failed = (failure: unknown, message: string, retry: () => void) =>
    ws.notify(
      knownAgentError(failure, locale) ?? message,
      { label: copy.tryAgain, run: retry },
      'error',
    );
  const named = (template: string, row: AgentListRow) => formatCopy(template, { name: row.name });
  const run = async (row: AgentListRow) => {
    const ownsNavigation = ws.beginNavigation();
    try {
      const started = await agentsApi.run(row.id);
      if (ownsNavigation()) ws.openThread(started.threadId);
    } catch (failure) {
      if (ownsNavigation()) failed(failure, named(copy.runFailed, row), () => void run(row));
    }
  };
  const switchOff = async (row: AgentListRow) => {
    try {
      await agentsApi.patch(row.id, { enabled: false });
      ws.notify(uiText('{value1} is switched off.', { value1: row.name }), null, 'success');
      list.refresh();
    } catch (failure) {
      failed(failure, named(copy.switchOffFailed, row), () => void switchOff(row));
    }
  };
  const archive = async (row: AgentListRow) => {
    try {
      await agentsApi.archive(row.id);
      ws.notify(uiText('{value1} is archived.', { value1: row.name }), null, 'success');
      list.refresh();
    } catch (failure) {
      failed(failure, named(copy.archiveFailed, row), () => void archive(row));
    }
  };
  // Archiving cannot be undone from here, so the row asks first (§7.1); a retry
  // from the toast does not ask again.
  const confirmArchive = async (row: AgentListRow) => {
    setArchiving({ row, busy: true });
    await archive(row);
    setArchiving(null);
  };
  const rows = (group: AgentGroup) => list.agents.filter((row) => row.group === group);
  const yours = rows('yours');
  const nothing = !list.loading && !list.error && list.agents.length === 0;
  if (!scope) return <LoadingState label={uiText('Loading agents…')} />;
  return (
    <div className="ws-agents">
      <PageHeading
        eyebrow={`${ws.companyName} ${shellCopy.workspace.toLocaleLowerCase()}`}
        title={uiText('Agents')}
        description={uiText(
          'Agents do work for you on a schedule or when you ask. They follow your company’s Skills.',
        )}
      >
        <StartInChatButton
          label={uiText('Create in chat')}
          onClick={() => beginAgentCreation(ws)}
        />
      </PageHeading>
      <input
        type="text"
        className="ws-resource-search"
        aria-label={copy.searchAgents}
        placeholder={copy.searchAgents}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {list.error && <ErrorState message={list.error} onRetry={list.refresh} />}
      {list.loading && !list.agents.length && <LoadingState label={uiText('Loading agents…')} />}
      {nothing && query.trim() ? (
        <p className="ws-page-note">
          {uiText('No agent matches. Try a Skill name or what the agent should do.')}
        </p>
      ) : (
        <div className="ws-agents-list">
          {!list.loading && !list.error && !yours.length && !query.trim() && (
            <>
              <div className="ws-agents-hint">
                <span className="ws-record-symbol">
                  <WorkspaceIcon name="plus" size={20} />
                </span>
                <span>
                  <strong>{uiText('Nothing of your own yet')}</strong>
                  <small>
                    {uiRich(
                      'Tell Chat what an agent should do, or copy one of the company’s agents below.{value1}',
                      { value1: ' ' },
                    )}
                  </small>
                </span>
              </div>
              <TemplateGallery limit={3} target="agent" />
            </>
          )}
          {GROUPS.map(({ group, label }) => {
            const members = rows(group);
            if (!members.length) return null;
            return (
              <section
                key={group}
                className="ws-agents-section"
                aria-label={label ?? uiText('Your agents')}
              >
                {label && <GroupLine label={label} />}
                {members.map((row) => (
                  <AgentRow
                    key={row.id}
                    row={row}
                    onRun={run}
                    onSwitchOff={switchOff}
                    onArchive={(asked) => setArchiving({ row: asked, busy: false })}
                  />
                ))}
              </section>
            );
          })}
        </div>
      )}
      {archiving && (
        <ConfirmDialog
          title={copy.archiveTitle}
          description={named(copy.archiveDescription, archiving.row)}
          confirmLabel={named(copy.archiveConfirm, archiving.row)}
          busy={archiving.busy}
          onCancel={() => setArchiving(null)}
          onConfirm={() => void confirmArchive(archiving.row)}
        />
      )}
    </div>
  );
}
